import { forwardRef } from "react";

import { useFieldControl } from "../Field/FieldContext";
import {
  InputAdornment,
  InputControl,
  InputFieldRow,
  InputTrailingAction,
  StyledBaseInput,
} from "./foundations/inputStyles";
import type { BaseInputProps } from "./foundations/inputTypes";

type InternalBaseInputProps = BaseInputProps & {
  fieldControlName?: string;
};

/**
 * Internal input shell used by TextInput and NumberInput.
 */
export const BaseInput = forwardRef<HTMLInputElement, InternalBaseInputProps>(
  (
    {
      fieldControlName = "BaseInput",
      inputSize = "md",
      fullWidth = true,
      className,
      style,
      autoComplete = "off",
      autoCorrect = "off",
      autoCapitalize = "off",
      spellCheck = false,
      rightAdornment,
      trailingAction,
      onEnterPress,
      onKeyDown,
      readOnly,
      disabled,
      invalid,
      id,
      ...rest
    },
    ref,
  ) => {
    const { controlId, disabled: isDisabled } = useFieldControl({
      componentName: fieldControlName,
      id,
      disabled,
    });

    // Some browsers report IME confirmation as Enter with keyCode 229.
    const handleKeyDown: BaseInputProps["onKeyDown"] = (event) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key !== "Enter") return;
      if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
      onEnterPress?.(event);
    };

    return (
      <InputFieldRow $fullWidth={fullWidth} className={className} style={style}>
        <InputControl
          $inputSize={inputSize}
          $invalid={invalid}
          $disabled={isDisabled}
          $readOnly={readOnly}
          $fullWidth={fullWidth}
        >
          <StyledBaseInput
            ref={ref}
            id={controlId}
            disabled={isDisabled}
            readOnly={readOnly}
            aria-invalid={invalid || undefined}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            autoCapitalize={autoCapitalize}
            spellCheck={spellCheck}
            onKeyDown={handleKeyDown}
            {...rest}
          />
          {rightAdornment ? <InputAdornment>{rightAdornment}</InputAdornment> : null}
        </InputControl>
        {trailingAction ? (
          <InputTrailingAction inert={isDisabled || undefined}>
            {trailingAction}
          </InputTrailingAction>
        ) : null}
      </InputFieldRow>
    );
  },
);

BaseInput.displayName = "BaseInput";
